export interface CropAttributeOption {
  id: number | string;
  code: string;
  title: string;
  value: string;
  sortOrder?: number | null;
}

export interface CropAttributeOptionInput {
  id?: number | string;
  code: string;
  title: string;
  sortOrder?: number | null;
}

export type CropAttributeScopeType = 'system' | 'save';

export type TaxonomyScope = 'family' | 'genus' | 'species' | 'cultivar' | 'crop';

export interface CropAttributeSummary {
  id: number | string;
  name: string;
  description?: string | null;
  scopeType?: CropAttributeScopeType | string | null;
  saveId?: string | null;
  options?: CropAttributeOption[];
}

export interface CropAttribute extends CropAttributeSummary {
  bindings?: CropAttributeBinding[];
}

export interface CropAttributeValue {
  attributeId: number | string;
  name?: string | null;
  description?: string | null;
  value?: string | null;
  scope?: TaxonomyScope | string | null;
  choiceId?: number | string | null;
  choiceValue?: string | null;
  choiceCode?: string | null;
  choiceTitle?: string | null;
  customValue?: string | null;
  familyId?: number | string | null;
  genusId?: number | string | null;
  speciesId?: number | string | null;
  cultivarId?: number | string | null;
  cropId?: number | string | null;
  bindingId?: number | string | null; // ID привязки на уровне crop (для удаления)
  valueObject?: CropAttributeValueObjectPayload | null;
}

export interface CropAttributeBinding {
  id?: number | string | null;
  attributeId: number | string;
  familyId?: number | string | null;
  genusId?: number | string | null;
  speciesId?: number | string | null;
  cultivarId?: number | string | null;
  cropId?: number | string | null;
  scope?: TaxonomyScope | string | null;
  choiceId?: number | string | null;
  choiceValue?: string | null;
  choiceCode?: string | null;
  choiceTitle?: string | null;
  customValue?: string | null;
  value?: string | null;
}

export interface TaxonomyAttributeItem {
  attribute: CropAttributeSummary;
  binding?: CropAttributeBinding | null;
  sourceScope?: TaxonomyScope | null;
  inherited?: boolean;
}

export interface Crop {
  id: number | string;
  storeId?: string;
  farmId?: string | null;
  speciesId?: number | string | null;
  speciesName?: string | null;
  cultivarId?: number | string | null;
  cultivarName?: string | null;
  latinName: string;
  name: string;
  plantingsCount?: number;
  attributes?: CropAttributeValue[];
}

export interface CropQueryParams {
  farmId: string;
  search?: string;
  speciesId?: number | string;
  cultivarId?: number | string;
  limit?: number;
  offset?: number;
}

export interface CropAttributeValueObjectPayload {
  choiceId?: number | string | null;
  custom?: string | null;
  customValue?: string | null;
  value?: string | null;
}

export type CropAttributeValueInput = {
  attributeId?: number | string;
  name?: string;
  description?: string | null;
  choiceId?: number | string | null;
  customValue?: string | null;
  value?: string | CropAttributeValueObjectPayload | null;
};

export type CropAttributesSetPayload = {
  attributes: CropAttributeValueInput[];
};

export type CropAttributeQueryParams = {
  saveId: string;
  search?: string;
  scopeType?: CropAttributeScopeType | string;
};

export type AvailableAttributesQueryParams = {
  scope: TaxonomyScope;
  scopeId: number | string;
  saveId: string;
};

export interface CropAttributeCreatePayload {
  name: string;
  description?: string | null;
  scopeType?: CropAttributeScopeType;
  options?: CropAttributeOptionInput[] | null;
  binding?: {
    scope: TaxonomyScope;
    scopeId: number | string;
  } | null;
}

export interface CropAttributeUpdatePayload {
  name?: string;
  description?: string | null;
  options?: CropAttributeOptionInput[] | null;
}

export type CropPayload = {
  farmId: string;
  speciesId: number | string;
  cultivarId?: number | string | null;
  latinName: string;
  name: string;
  attributes?: CropAttributeValueInput[];
};

export type CropUpdatePayload = Partial<
  Omit<CropPayload, 'farmId'>
> & {
  attributes?: CropAttributeValueInput[];
};

export const adaptCropAttributeOption = (data: any): CropAttributeOption => ({
  id: data.id,
  code: data.code ?? '',
  title: data.title ?? data.value ?? '',
  value: data.value ?? data.title ?? '',
  sortOrder: data.sortOrder ?? data.sort_order ?? null,
});

export const adaptCropAttributeSummary = (data: any): CropAttributeSummary => ({
  id: data.id,
  name: data.name ?? '',
  description: data.description ?? null,
  scopeType: data.scopeType ?? data.scope_type ?? 'system',
  saveId: data.saveId ?? data.save_id ?? null,
  options: (() => {
    const rawOptions = Array.isArray(data.options) ? data.options : Array.isArray(data.choices) ? data.choices : [];
    return rawOptions.map((item: any) => adaptCropAttributeOption(item));
  })(),
});

export const adaptCropAttribute = (data: any): CropAttribute => ({
  ...adaptCropAttributeSummary(data),
  bindings: Array.isArray(data.bindings)
    ? data.bindings.map((binding: any) => adaptCropAttributeBinding(binding))
    : [],
});

const adaptAttributeValuePayload = (value: any): CropAttributeValueObjectPayload | null => {
  if (!value || typeof value !== 'object') return null;
  return {
    choiceId: value.choiceId ?? value.choice_id ?? null,
    custom: value.custom ?? null,
    customValue: value.customValue ?? value.custom_value ?? null,
    value: value.value ?? null,
  };
};

export const adaptCropAttributeValue = (data: any): CropAttributeValue => ({
  attributeId: data.attributeId ?? data.attribute_id,
  name: data.name ?? null,
  description: data.description ?? null,
  value: typeof data.value === 'object' ? data.value?.value ?? null : data.value ?? null,
  scope: data.scope ?? null,
  choiceId: data.choiceId ?? data.choice_id ?? data.choice?.id ?? null,
  choiceValue: data.choiceValue ?? data.choice_value ?? data.choice?.value ?? null,
  choiceCode: data.choiceCode ?? data.choice_code ?? data.choice?.code ?? null,
  choiceTitle: data.choiceTitle ?? data.choice_title ?? data.choice?.title ?? null,
  customValue: data.customValue ?? data.custom_value ?? null,
  familyId: data.familyId ?? data.family_id ?? null,
  genusId: data.genusId ?? data.genus_id ?? null,
  speciesId: data.speciesId ?? data.species_id ?? null,
  cultivarId: data.cultivarId ?? data.cultivar_id ?? null,
  cropId: data.cropId ?? data.crop_id ?? null,
  bindingId: data.bindingId ?? data.binding_id ?? null,
  valueObject: adaptAttributeValuePayload(data.value),
});

export const adaptCropAttributeBinding = (data: any): CropAttributeBinding => ({
  id: data.id ?? null,
  attributeId: data.attributeId ?? data.attribute_id ?? data.cropAttributeId ?? data.crop_attribute_id ?? data.id ?? null,
  familyId: data.familyId ?? data.family_id ?? null,
  genusId: data.genusId ?? data.genus_id ?? null,
  speciesId: data.speciesId ?? data.species_id ?? null,
  cultivarId: data.cultivarId ?? data.cultivar_id ?? null,
  cropId: data.cropId ?? data.crop_id ?? null,
  scope: data.scope ?? null,
  choiceId: data.choiceId ?? data.choice_id ?? null,
  choiceValue: data.choiceValue ?? data.choice_value ?? null,
  choiceCode: data.choiceCode ?? data.choice_code ?? null,
  choiceTitle: data.choiceTitle ?? data.choice_title ?? data.choiceValue ?? data.choice_value ?? null,
  customValue: data.customValue ?? data.custom_value ?? null,
  value: data.value ?? null,
});

export const adaptTaxonomyAttributeItem = (data: any): TaxonomyAttributeItem => ({
  attribute: adaptCropAttributeSummary(data.attribute ?? {}),
  binding: data.binding ? adaptCropAttributeBinding(data.binding) : undefined,
  sourceScope: data.sourceScope ?? data.source_scope ?? null,
  inherited: Boolean(data.inherited ?? data.is_inherited ?? false),
});


export const adaptCrop = (data: any): Crop => ({
  id: data.id,
  storeId: data.storeId ?? data.store_id ?? undefined,
  farmId: data.farmId ?? data.farm_id ?? null,
  speciesId: data.speciesId ?? data.species_id ?? null,
  speciesName: data.speciesName ?? data.species_name ?? null,
  cultivarId: data.cultivarId ?? data.cultivar_id ?? null,
  cultivarName: data.cultivarName ?? data.cultivar_name ?? null,
  latinName: data.latinName ?? data.latin_name ?? '',
  name: data.name ?? '',
  plantingsCount: data.plantingsCount ?? data.plantings_count ?? 0,
  attributes: Array.isArray(data.attributes)
    ? data.attributes.map((item: any) => adaptCropAttributeValue(item))
    : undefined,
});

export interface CropTaxonomyCultivar {
  id: number | string;
  latinName: string;
  name: string;
}

export interface CropTaxonomySpecies {
  id: number | string;
  latinName: string;
  name: string;
  cultivars: CropTaxonomyCultivar[];
}

export interface CropTaxonomyGenus {
  id: number | string;
  latinName: string;
  name: string;
  species: CropTaxonomySpecies[];
}

export interface CropTaxonomyFamily {
  id: number | string;
  latinName: string;
  name: string;
  genera: CropTaxonomyGenus[];
}

export interface CropTaxonomyTree {
  families: CropTaxonomyFamily[];
}

const adaptCultivar = (cultivar: any): CropTaxonomyCultivar => ({
  id: cultivar.id,
  latinName: cultivar.latinName ?? cultivar.latin_name ?? '',
  name: cultivar.name ?? '',
});

const adaptSpecies = (species: any): CropTaxonomySpecies => ({
  id: species.id,
  latinName: species.latinName ?? species.latin_name ?? '',
  name: species.name ?? '',
  cultivars: Array.isArray(species.cultivars)
    ? species.cultivars.map((item: any) => adaptCultivar(item))
    : [],
});

const adaptGenus = (genus: any): CropTaxonomyGenus => ({
  id: genus.id,
  latinName: genus.latinName ?? genus.latin_name ?? '',
  name: genus.name ?? '',
  species: Array.isArray(genus.species)
    ? genus.species.map((item: any) => adaptSpecies(item))
    : [],
});

const adaptFamily = (family: any): CropTaxonomyFamily => ({
  id: family.id,
  latinName: family.latinName ?? family.latin_name ?? '',
  name: family.name ?? '',
  genera: Array.isArray(family.genera)
    ? family.genera.map((item: any) => adaptGenus(item))
    : [],
});

export const adaptCropTaxonomyTree = (data: any): CropTaxonomyTree => ({
  families: Array.isArray(data?.families)
    ? data.families.map((family: any) => adaptFamily(family))
    : [],
});
